/*
 * FILE: unified_ui/src/lib/botSuiteClient.ts
 * PURPOSE: Thin fetch wrapper for POSTing to /api/cmd (NATS command proxy via webBridge).
 * OWNS: API call serialization, timeout handling, 401 session expiry detection.
 * EXPORTS: call, Envelope type.
 * NOTES: The actor context is added server-side by webBridge — the SPA does NOT send actor manually.
 *        Sends the bearer session header when present (embedded third-party iframes).
 */
import { authHeaders } from './sessionToken'

export type Envelope<T> = { success: true; data: T } | { success: false; error: string };

const SESSION_EXPIRED_EVENT = "botsuite:session-expired";

export async function call<TReq = unknown, TRes = unknown>(
  subject: string,
  payload?: TReq,
  timeoutMs = 30000,
): Promise<Envelope<TRes>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch("/api/cmd", {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      credentials: "include",
      body: JSON.stringify({ subject, payload }),
      signal: controller.signal,
    });

    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      return { success: false, error: "session_expired" };
    }

    const json = await res.json();
    return json as Envelope<TRes>;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    if (message === "AbortError" || message.includes("abort")) {
      return { success: false, error: "timeout" };
    }
    return { success: false, error: err instanceof Error ? err.message : "Network error" };
  } finally {
    clearTimeout(timer);
  }
}
